import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to get WebSocket URL from API URL
const getWebSocketUrl = () => {
  const apiUrl = API || window.location.origin;
  const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
  const wsHost = apiUrl.replace(/^https?:\/\//, '');
  return `${wsProtocol}://${wsHost}/ws/sync`;
};

/**
 * BusinessReportDownload Component
 * Allows downloading a comprehensive business report PDF with date filtering
 */
const BusinessReportDownload = ({ API }) => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const token = localStorage.getItem('dashboard_token');
      let url = `${API}/api/business-report/download`;
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const dateStr = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `Business_Report_${dateStr}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading report:', error);
      alert('Error generating report. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };
  
  // Quick date range setters
  const setThisMonth = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    setFromDate(firstDay.toISOString().split('T')[0]);
    setToDate(now.toISOString().split('T')[0]);
  };
  
  const setLastMonth = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
    setFromDate(firstDay.toISOString().split('T')[0]);
    setToDate(lastDay.toISOString().split('T')[0]);
  };
  
  const setLast3Months = () => {
    const now = new Date();
    const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, 1);
    setFromDate(threeMonthsAgo.toISOString().split('T')[0]);
    setToDate(now.toISOString().split('T')[0]);
  };
  
  const setAllTime = () => {
    setFromDate('');
    setToDate('');
  };
  
  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(184,134,11,0.15) 0%, rgba(184,134,11,0.05) 100%)',
      border: '1px solid rgba(184,134,11,0.4)',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '20px'
    }}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px'}}>
        <div>
          <h3 style={{margin: '0 0 8px', color: '#b8860b', display: 'flex', alignItems: 'center', gap: '8px'}}>
            📊 Business Analysis Report
          </h3>
          <p style={{margin: 0, fontSize: '13px', color: '#9ca3af'}}>
            Download comprehensive report with Profit Analysis, Credit Accounts & Product Sales Breakdown
          </p>
        </div>
        
        <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
          {/* Date Range Inputs */}
          <div style={{display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
              <label style={{fontSize: '12px', color: '#9ca3af'}}>From:</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                style={{
                  padding: '6px 10px',
                  background: 'rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '6px',
                  color: '#e5e7eb',
                  fontSize: '13px'
                }}
              />
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
              <label style={{fontSize: '12px', color: '#9ca3af'}}>To:</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                style={{
                  padding: '6px 10px',
                  background: 'rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '6px',
                  color: '#e5e7eb',
                  fontSize: '13px'
                }}
              />
            </div>
          </div>
          
          {/* Quick Date Range Buttons */}
          <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
            <button
              onClick={setThisMonth}
              style={{
                padding: '4px 10px',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '4px',
                color: '#9ca3af',
                fontSize: '11px',
                cursor: 'pointer'
              }}
            >
              This Month
            </button>
            <button
              onClick={setLastMonth}
              style={{
                padding: '4px 10px',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '4px',
                color: '#9ca3af',
                fontSize: '11px',
                cursor: 'pointer'
              }}
            >
              Last Month
            </button>
            <button
              onClick={setLast3Months}
              style={{
                padding: '4px 10px',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '4px',
                color: '#9ca3af',
                fontSize: '11px',
                cursor: 'pointer'
              }}
            >
              Last 3 Months
            </button>
            <button
              onClick={setAllTime}
              style={{
                padding: '4px 10px',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '4px',
                color: '#9ca3af',
                fontSize: '11px',
                cursor: 'pointer'
              }}
            >
              All Time
            </button>
          </div>
        </div>
        
        {/* Download Button */}
        <button
          onClick={handleDownload}
          disabled={isDownloading}
          style={{
            padding: '12px 24px',
            background: isDownloading ? 'rgba(184,134,11,0.3)' : 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '14px',
            cursor: isDownloading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: isDownloading ? 'none' : '0 4px 12px rgba(184,134,11,0.3)'
          }}
        >
          {isDownloading ? (
            <>
              <span style={{animation: 'spin 1s linear infinite', display: 'inline-block'}}>⏳</span>
              Generating...
            </>
          ) : (
            <>
              📥 Download Report
            </>
          )}
        </button>
      </div>
      
      {/* Report Contents Info */}
      <div style={{
        marginTop: '15px',
        padding: '12px',
        background: 'rgba(0,0,0,0.2)',
        borderRadius: '8px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '10px'
      }}>
        <div style={{fontSize: '12px', color: '#9ca3af'}}>
          <strong style={{color: '#e5e7eb'}}>Page 1:</strong> Business Analysis (Sales, Costs, Gross/Net Profit)
        </div>
        <div style={{fontSize: '12px', color: '#9ca3af'}}>
          <strong style={{color: '#e5e7eb'}}>Page 2:</strong> Customer Credit Summary (Name + Total Owed)
        </div>
        <div style={{fontSize: '12px', color: '#9ca3af'}}>
          <strong style={{color: '#e5e7eb'}}>Page 3:</strong> Products Sold by Price (Full Breakdown)
        </div>
      </div>
    </div>
  );
};

/**
 * DashboardUserView Component
 * Displays the business dashboard for the `zobayr` dashboard user.
 * Contains Summary, Admin, and Review tabs.
 * 
 * IMPORTANT: This dashboard calculates data DIRECTLY from fulfilledOrders
 * to ensure it shows the EXACT SAME figures as the admin dashboard.
 * Data is synced INSTANTLY via WebSocket when any changes occur.
 */
const DashboardUserView = ({
  dashboardData,
  handleDashboardLogout,
  fulfilledOrders,
  orders,
  creditAccountsOrders = [],
  customerAccountPayments = {},
  setDashboardUserInAdminMode,
  setIsAdminLoggedIn,
  setCurrentPage,
  setActiveTab,
  setIsDashboardUser,
  setLoggedInAdminUsername,
  adminPermissions = {},
  loggedInAdminUsername = '',
  refreshDashboardData
}) => {
  const [dashboardTab, setDashboardTab] = useState('summary');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const lastUpdateRef = useRef(null);
  const pollIntervalRef = useRef(null);
  
  // Check if user is zobayr (has full access) or just has view permission
  const isZobayr = loggedInAdminUsername.toLowerCase() === 'zobayr';
  const isSpectator = adminPermissions.role === 'spectator';
  const hasFullAccess = isZobayr; // Only zobayr has full dashboard access
  const isViewOnly = !isZobayr && adminPermissions.canAccessDashboard; // Others with permission are view-only
  
  // 🔒 Action History state
  const [actionHistory, setActionHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySummary, setHistorySummary] = useState(null);
  
  // 🔒 Load Action History
  const loadActionHistory = async () => {
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('dashboard_token') || localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/api/action-history?limit=200`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setActionHistory(response.data.history || []);
      
      // Also load summary
      const summaryResponse = await axios.get(`${API}/api/action-history/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setHistorySummary(summaryResponse.data);
    } catch (error) {
      console.error('Error loading action history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };
  
  // 📡 Real-Time Sync via Polling (1 second interval)
  // Silent background refresh - NO loading states to avoid UI flicker
  useEffect(() => {
    let mounted = true;
    
    const pollForUpdates = async () => {
      if (!mounted) return;
      
      try {
        const response = await axios.get(`${API}/api/sync/poll`);
        const { last_update } = response.data;
        
        // Check if there's a new update
        if (last_update && last_update.timestamp !== lastUpdateRef.current) {
          const isFirstPoll = lastUpdateRef.current === null;
          lastUpdateRef.current = last_update.timestamp;
          
          // Only trigger SILENT refresh if this isn't the first poll
          if (!isFirstPoll && refreshDashboardData) {
            console.log('📡 Real-time update detected:', last_update.type);
            // Silent refresh - no loading state, just update data in background
            refreshDashboardData();
            setLastRefresh(new Date());
          }
          
          setWsConnected(true); // Show "LIVE" when polling is working
        }
      } catch (error) {
        console.warn('📡 Polling error:', error.message);
        setWsConnected(false);
      }
    };
    
    // Initial poll
    pollForUpdates();
    
    // Poll every 1 second for real-time updates (silent background)
    pollIntervalRef.current = setInterval(pollForUpdates, 1000);
    
    return () => {
      mounted = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [refreshDashboardData]);
  
  /**
   * LIVE CALCULATION: Calculate sales figures directly from fulfilledOrders
   * This ensures INSTANT sync with admin dashboard - same data, same calculations
   * NO FILTERING - must match admin exactly
   */
  const liveCalculations = useMemo(() => {
    const orders = fulfilledOrders || [];
    
    let totalSales = 0;
    let totalCash = 0;
    let totalBank = 0;
    let outstandingCredit = 0;
    
    // Build credit accounts data from creditAccountsOrders (bulletproof source)
    // 💰 IRON-CLAD: Each guest customer (customer_id=null) is treated separately
    const creditCustomers = {};
    const productSales = {};
    
    orders.forEach(order => {
      const grandTotal = parseFloat(order.grand_total || 0) || 0;
      const cashPaid = parseFloat(order.cash_paid_amount || 0) || 0;
      const bankPaid = parseFloat(order.bank_paid_amount || 0) || 0;
      
      totalSales += grandTotal;
      totalCash += cashPaid;
      totalBank += bankPaid;
      
      // Track top selling products from order items
      const items = order.items || [];
      items.forEach(item => {
        const productName = item.product_name || item.name || 'Unknown';
        if (!productSales[productName]) productSales[productName] = 0;
        productSales[productName] += parseFloat(item.total_price || item.price || 0) || 0;
      });
    });
    
    // 💰 IRON-CLAD: Use creditAccountsOrders as single source of truth for outstanding credit.
    // This guarantees: ANY bill with outstanding balance > 0 (regardless of payment_method or status)
    // is counted, identical to the Credit Accounts page.
    (creditAccountsOrders || []).forEach(order => {
      const outstanding = parseFloat(order.outstanding_balance || 0) || 0;
      if (outstanding <= 0) return;
      outstandingCredit += outstanding;
      
      // Composite customer key (keeps each guest customer unique)
      const isGuest = !order.customer_id;
      const customerKey = isGuest
        ? `guest:${order.customer_number || order.id}`
        : order.customer_id;
      
      if (!creditCustomers[customerKey]) {
        creditCustomers[customerKey] = {
          customer_id: order.customer_id || null,
          isGuest,
          customer_number: order.customer_number || 'N/A',
          name: order.customer_name || order.company_name || 'Unknown',
          company: order.company_name || '',
          phone: order.customer_contact || order.customer_phone || '',
          total_credit_taken: 0,
          total_paid: 0,
          outstanding_balance: 0,
          orders: []
        };
      }
      
      const grandTotal = parseFloat(order.grand_total || 0) || 0;
      const totalPaid = grandTotal - outstanding;
      creditCustomers[customerKey].total_credit_taken += grandTotal;
      creditCustomers[customerKey].total_paid += totalPaid;
      creditCustomers[customerKey].outstanding_balance += outstanding;
      creditCustomers[customerKey].orders.push({
        id: order.id,
        date: order.date,
        grand_total: grandTotal,
        paid: totalPaid,
        balance: outstanding
      });
    });
    
    // Apply account-level (lump sum) payments to each customer.
    // 💰 IRON-CLAD: Cap deduction at the customer's gross outstanding (never go negative)
    // to mirror the Credit Accounts page exactly. Account payments for customers with
    // no remaining outstanding contribute 0 to the grand total.
    let totalAppliedAccountPayments = 0;
    Object.entries(creditCustomers).forEach(([key, group]) => {
      if (!group.isGuest && group.customer_id) {
        const acct = customerAccountPayments[group.customer_id];
        const acctTotal = parseFloat((acct && acct.total) || 0);
        if (acctTotal > 0) {
          const applied = Math.min(acctTotal, group.outstanding_balance);
          group.total_paid += applied;
          group.outstanding_balance = Math.max(0, group.outstanding_balance - acctTotal);
          totalAppliedAccountPayments += applied;
        }
      }
    });
    outstandingCredit = Math.max(0, outstandingCredit - totalAppliedAccountPayments);
    
    // Convert to array and sort by outstanding balance (highest first), then by name
    const creditAccountsList = Object.values(creditCustomers).sort((a, b) => {
      if (b.outstanding_balance !== a.outstanding_balance) {
        return b.outstanding_balance - a.outstanding_balance;
      }
      return (a.name || '').localeCompare(b.name || '');
    });
    
    // Top selling products (sorted by value, top 10)
    const topProducts = Object.entries(productSales)
      .map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
    
    return {
      total_sales: Math.round(totalSales * 100) / 100,
      total_cash: Math.round(totalCash * 100) / 100,
      total_bank: Math.round(totalBank * 100) / 100,
      outstanding_credit: Math.round(outstandingCredit * 100) / 100,
      total_orders: orders.length,
      credit_accounts: creditAccountsList,
      active_credit_accounts: creditAccountsList.filter(c => c.outstanding_balance > 0).length,
      top_products: topProducts
    };
  }, [fulfilledOrders, creditAccountsOrders, customerAccountPayments]);
  
  // Manual refresh data handler with loading state (only for button clicks)
  const handleRefresh = useCallback(async () => {
    if (refreshDashboardData && !isRefreshing) {
      setIsRefreshing(true);
      await refreshDashboardData();
      setLastRefresh(new Date());
      setIsRefreshing(false);
    }
  }, [refreshDashboardData, isRefreshing]);
  
  // Note: Auto-refresh removed - polling handles real-time updates silently
  
  // Refresh when component mounts
  useEffect(() => {
    if (refreshDashboardData) {
      handleRefresh();
    }
  }, []);
  const [detailModal, setDetailModal] = useState(null);
  const [paymentReview, setPaymentReview] = useState({ transactions: [], summary: {} });
  const [loadingReview, setLoadingReview] = useState(false);
  const [selectedBillId, setSelectedBillId] = useState(null);
  
  // Load payment review - complete transaction history
  const loadPaymentReview = async () => {
    try {
      setLoadingReview(true);
      const token = localStorage.getItem('dashboard_token');
      const response = await axios.get(`${API}/api/payment-review`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPaymentReview(response.data || { transactions: [], summary: {} });
    } catch (error) {
      console.error('Error loading payment review:', error);
    } finally {
      setLoadingReview(false);
    }
  };
  
  // Approve a transaction
  const approveTransaction = async (txnId) => {
    try {
      const token = localStorage.getItem('dashboard_token');
      await axios.post(`${API}/api/payment-review/approve/${txnId}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadPaymentReview(); // Refresh list
    } catch (error) {
      console.error('Error approving transaction:', error);
    }
  };
  
  // Reject a transaction - removes the payment and restores credit balance
  const rejectTransaction = async (txn) => {
    const confirmMsg = `⚠️ REJECT PAYMENT\n\nAre you sure you want to reject this payment?\n\nCustomer: ${txn.customer_name}\nAmount: £${txn.amount?.toFixed(2)}\nType: ${txn.type === 'PAYMENT_CASH' ? 'Cash' : txn.type === 'PAYMENT_BANK' ? 'Bank' : 'Credit Payment'}\n\nThis will:\n• Remove the payment from the order\n• Restore the credit balance\n• The bill will go back to its original state before this payment`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const token = localStorage.getItem('dashboard_token');
      const response = await axios.post(`${API}/api/payment-review/reject/${txn.id}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert(`✅ Payment Rejected\n\n${response.data?.message || 'Payment removed successfully.'}\nNew credit balance: £${response.data?.new_credit_balance?.toFixed(2) || '0.00'}`);
      loadPaymentReview(); // Refresh list
      // Also refresh dashboard data to update totals
      if (refreshDashboardData) {
        refreshDashboardData();
      }
    } catch (error) {
      console.error('Error rejecting transaction:', error);
      alert('Error rejecting payment: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  // Approve all transactions
  const approveAllTransactions = async () => {
    if (!window.confirm('Approve all current transactions? They will no longer appear in the review list.')) return;
    try {
      const token = localStorage.getItem('dashboard_token');
      const response = await axios.post(`${API}/api/payment-review/approve-all`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert(response.data?.message || 'All transactions approved successfully!');
      loadPaymentReview(); // Refresh list
    } catch (error) {
      console.error('Error approving all:', error);
      alert('Error approving transactions: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  // View bill modal - find the order by ID
  const viewBill = (orderId) => {
    const order = fulfilledOrders.find(o => o.id === orderId) || orders.find(o => o.id === orderId);
    if (order) {
      setSelectedBillId(order);
    } else {
      alert('Bill not found. It may have been deleted or archived.');
    }
  };
  
  if (!dashboardData) return <div className="loading"><div className="spinner"></div></div>;
  
  // Detail Modal Component - USES liveCalculations for accurate sync with admin
  const DetailModal = ({ type, onClose }) => {
    const getContent = () => {
      switch(type) {
        case 'total-sales':
          return {
            title: '💰 Total Sales Breakdown',
            color: '#22c55e',
            content: (
              <div>
                <div style={{display: 'grid', gap: '15px'}}>
                  <div style={{background: 'rgba(255,255,255,0.05)', padding: '20px', borderRadius: '10px'}}>
                    <div style={{fontSize: '14px', color: '#9ca3af', marginBottom: '5px'}}>Gross Sales (All Fulfilled Orders)</div>
                    <div style={{fontSize: '28px', fontWeight: '700', color: '#22c55e'}}>£{liveCalculations.total_sales?.toLocaleString() || '0'}</div>
                  </div>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '10px'}}>
                      <div style={{fontSize: '12px', color: '#9ca3af'}}>Cash Received</div>
                      <div style={{fontSize: '20px', fontWeight: '600', color: '#3b82f6'}}>£{liveCalculations.total_cash?.toLocaleString() || '0'}</div>
                    </div>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '10px'}}>
                      <div style={{fontSize: '12px', color: '#9ca3af'}}>Bank Received</div>
                      <div style={{fontSize: '20px', fontWeight: '600', color: '#a855f7'}}>£{liveCalculations.total_bank?.toLocaleString() || '0'}</div>
                    </div>
                  </div>
                  <div style={{background: 'rgba(239,68,68,0.1)', padding: '15px', borderRadius: '10px', border: '1px solid rgba(239,68,68,0.3)'}}>
                    <div style={{fontSize: '12px', color: '#9ca3af'}}>Outstanding (Unpaid)</div>
                    <div style={{fontSize: '20px', fontWeight: '600', color: '#ef4444'}}>£{liveCalculations.outstanding_credit?.toLocaleString() || '0'}</div>
                  </div>
                  <div style={{marginTop: '10px', padding: '15px', background: 'rgba(184,134,11,0.1)', borderRadius: '10px', border: '1px solid rgba(184,134,11,0.3)'}}>
                    <div style={{fontSize: '12px', color: '#9ca3af', marginBottom: '5px'}}>Collection Rate</div>
                    <div style={{fontSize: '18px', fontWeight: '600', color: '#b8860b'}}>
                      {liveCalculations.total_sales > 0 ? 
                        (((liveCalculations.total_cash + liveCalculations.total_bank) / liveCalculations.total_sales) * 100).toFixed(1) : 0}%
                    </div>
                  </div>
                </div>
              </div>
            )
          };
        case 'cash':
          return {
            title: '💵 Cash Received Details',
            color: '#3b82f6',
            content: (
              <div>
                <div style={{background: 'rgba(255,255,255,0.05)', padding: '20px', borderRadius: '10px', marginBottom: '15px'}}>
                  <div style={{fontSize: '14px', color: '#9ca3af', marginBottom: '5px'}}>Total Cash Collected</div>
                  <div style={{fontSize: '32px', fontWeight: '700', color: '#3b82f6'}}>£{liveCalculations.total_cash?.toLocaleString() || '0'}</div>
                </div>
                <div style={{background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '10px'}}>
                  <div style={{fontSize: '12px', color: '#9ca3af', marginBottom: '10px'}}>Cash as % of Total Sales</div>
                  <div style={{width: '100%', height: '20px', background: 'rgba(255,255,255,0.1)', borderRadius: '10px', overflow: 'hidden'}}>
                    <div style={{
                      width: `${liveCalculations.total_sales > 0 ? (liveCalculations.total_cash / liveCalculations.total_sales) * 100 : 0}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                      borderRadius: '10px'
                    }}></div>
                  </div>
                  <div style={{fontSize: '14px', color: '#3b82f6', marginTop: '8px', fontWeight: '600'}}>
                    {liveCalculations.total_sales > 0 ? ((liveCalculations.total_cash / liveCalculations.total_sales) * 100).toFixed(1) : 0}%
                  </div>
                </div>
              </div>
            )
          };
        case 'bank':
          return {
            title: '🏦 Bank Received Details',
            color: '#a855f7',
            content: (
              <div>
                <div style={{background: 'rgba(255,255,255,0.05)', padding: '20px', borderRadius: '10px', marginBottom: '15px'}}>
                  <div style={{fontSize: '14px', color: '#9ca3af', marginBottom: '5px'}}>Total Bank Transfers</div>
                  <div style={{fontSize: '32px', fontWeight: '700', color: '#a855f7'}}>£{liveCalculations.total_bank?.toLocaleString() || '0'}</div>
                </div>
                <div style={{background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '10px'}}>
                  <div style={{fontSize: '12px', color: '#9ca3af', marginBottom: '10px'}}>Bank as % of Total Sales</div>
                  <div style={{width: '100%', height: '20px', background: 'rgba(255,255,255,0.1)', borderRadius: '10px', overflow: 'hidden'}}>
                    <div style={{
                      width: `${liveCalculations.total_sales > 0 ? (liveCalculations.total_bank / liveCalculations.total_sales) * 100 : 0}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #a855f7, #c084fc)',
                      borderRadius: '10px'
                    }}></div>
                  </div>
                  <div style={{fontSize: '14px', color: '#a855f7', marginTop: '8px', fontWeight: '600'}}>
                    {liveCalculations.total_sales > 0 ? ((liveCalculations.total_bank / liveCalculations.total_sales) * 100).toFixed(1) : 0}%
                  </div>
                </div>
              </div>
            )
          };
        case 'credit':
          return {
            title: '📋 Outstanding Credit Details',
            color: '#ef4444',
            content: (
              <div>
                <div style={{background: 'rgba(239,68,68,0.1)', padding: '20px', borderRadius: '10px', marginBottom: '15px', border: '1px solid rgba(239,68,68,0.3)'}}>
                  <div style={{fontSize: '14px', color: '#9ca3af', marginBottom: '5px'}}>Total Outstanding</div>
                  <div style={{fontSize: '32px', fontWeight: '700', color: '#ef4444'}}>£{liveCalculations.outstanding_credit?.toLocaleString() || '0'}</div>
                </div>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
                  <div style={{background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '10px'}}>
                    <div style={{fontSize: '12px', color: '#9ca3af'}}>Credit Accounts</div>
                    <div style={{fontSize: '24px', fontWeight: '600', color: '#f59e0b'}}>{dashboardData?.customers?.active_credit_accounts || 0}</div>
                  </div>
                  <div style={{background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '10px'}}>
                    <div style={{fontSize: '12px', color: '#9ca3af'}}>Avg per Account</div>
                    <div style={{fontSize: '24px', fontWeight: '600', color: '#f59e0b'}}>
                      £{dashboardData?.customers?.active_credit_accounts > 0 ? 
                        (liveCalculations.outstanding_credit / dashboardData.customers.active_credit_accounts).toFixed(0) : '0'}
                    </div>
                  </div>
                </div>
                <div style={{marginTop: '15px', padding: '15px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px'}}>
                  <div style={{fontSize: '12px', color: '#9ca3af', marginBottom: '10px'}}>Outstanding as % of Sales</div>
                  <div style={{width: '100%', height: '20px', background: 'rgba(255,255,255,0.1)', borderRadius: '10px', overflow: 'hidden'}}>
                    <div style={{
                      width: `${liveCalculations.total_sales > 0 ? (liveCalculations.outstanding_credit / liveCalculations.total_sales) * 100 : 0}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #ef4444, #f87171)',
                      borderRadius: '10px'
                    }}></div>
                  </div>
                  <div style={{fontSize: '14px', color: '#ef4444', marginTop: '8px', fontWeight: '600'}}>
                    {liveCalculations.total_sales > 0 ? ((liveCalculations.outstanding_credit / liveCalculations.total_sales) * 100).toFixed(1) : 0}%
                  </div>
                </div>
              </div>
            )
          };
        case 'stock':
          return {
            title: '📦 Stock Value Details',
            color: '#b8860b',
            content: (
              <div>
                <div style={{background: 'rgba(184,134,11,0.1)', padding: '20px', borderRadius: '10px', marginBottom: '15px', border: '1px solid rgba(184,134,11,0.3)'}}>
                  <div style={{fontSize: '14px', color: '#9ca3af', marginBottom: '5px'}}>Total Inventory Value</div>
                  <div style={{fontSize: '32px', fontWeight: '700', color: '#b8860b'}}>£{dashboardData.stock?.total_stock_value?.toLocaleString() || '0'}</div>
                </div>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
                  <div style={{background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '10px'}}>
                    <div style={{fontSize: '12px', color: '#9ca3af'}}>Total Products</div>
                    <div style={{fontSize: '24px', fontWeight: '600', color: '#b8860b'}}>{dashboardData.stock?.total_products || 0}</div>
                  </div>
                  <div style={{background: 'rgba(239,68,68,0.1)', padding: '15px', borderRadius: '10px', border: '1px solid rgba(239,68,68,0.3)'}}>
                    <div style={{fontSize: '12px', color: '#9ca3af'}}>Low Stock Items</div>
                    <div style={{fontSize: '24px', fontWeight: '600', color: '#ef4444'}}>{dashboardData.stock?.low_stock_count || 0}</div>
                  </div>
                </div>
              </div>
            )
          };
        case 'orders':
          return {
            title: '📑 Orders Summary',
            color: '#06b6d4',
            content: (
              <div>
                <div style={{background: 'rgba(6,182,212,0.1)', padding: '20px', borderRadius: '10px', marginBottom: '15px', border: '1px solid rgba(6,182,212,0.3)'}}>
                  <div style={{fontSize: '14px', color: '#9ca3af', marginBottom: '5px'}}>Total Fulfilled Orders</div>
                  <div style={{fontSize: '32px', fontWeight: '700', color: '#06b6d4'}}>{liveCalculations.total_orders || 0}</div>
                </div>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
                  <div style={{background: 'rgba(34,197,94,0.1)', padding: '15px', borderRadius: '10px', border: '1px solid rgba(34,197,94,0.3)'}}>
                    <div style={{fontSize: '12px', color: '#9ca3af'}}>Fulfilled</div>
                    <div style={{fontSize: '24px', fontWeight: '600', color: '#22c55e'}}>{liveCalculations.total_orders || 0}</div>
                  </div>
                  <div style={{background: 'rgba(245,158,11,0.1)', padding: '15px', borderRadius: '10px', border: '1px solid rgba(245,158,11,0.3)'}}>
                    <div style={{fontSize: '12px', color: '#9ca3af'}}>Pending</div>
                    <div style={{fontSize: '24px', fontWeight: '600', color: '#f59e0b'}}>{dashboardData?.sales?.pending_orders || 0}</div>
                  </div>
                </div>
                <div style={{marginTop: '15px', background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '10px'}}>
                  <div style={{fontSize: '12px', color: '#9ca3af'}}>Average Order Value</div>
                  <div style={{fontSize: '24px', fontWeight: '600', color: '#06b6d4'}}>
                    £{liveCalculations.total_orders > 0 ? 
                      (liveCalculations.total_sales / liveCalculations.total_orders).toFixed(2) : '0'}
                  </div>
                </div>
              </div>
            )
          };
        default:
          return { title: 'Details', color: '#fff', content: <div>No data</div> };
      }
    };
    
    const modalData = getContent();
    
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10000
      }} onClick={onClose}>
        <div style={{
          background: 'linear-gradient(135deg, #1a2744 0%, #0f1a35 100%)',
          borderRadius: '16px', padding: '25px', maxWidth: '500px', width: '90%',
          border: `1px solid ${modalData.color}40`
        }} onClick={e => e.stopPropagation()}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
            <h2 style={{margin: 0, color: modalData.color, fontSize: '20px'}}>{modalData.title}</h2>
            <button onClick={onClose} style={{background: 'transparent', border: 'none', color: '#9ca3af', fontSize: '24px', cursor: 'pointer'}}>×</button>
          </div>
          {modalData.content}
        </div>
      </div>
    );
  };
  
  // Admin sections for the Admin tab
  const dashboardAdminSections = [
    { id: 'products', title: 'PRODUCTS', icon: '📦', description: 'View products and inventory', color: '#8B4513' },
    { id: 'customers', title: 'CUSTOMERS', icon: '👥', description: 'Customer database', color: '#4A90E2' },
    { id: 'orders', title: 'ORDERS', icon: '📋', description: 'Pending orders', color: '#5A67D8' },
    { id: 'fulfilled-orders', title: 'FULFILLED ORDERS', icon: '✅', description: 'Completed orders', color: '#48BB78' },
    { id: 'credit-accounts', title: 'CREDIT ACCOUNTS', icon: '💳', description: 'Credit account management', color: '#38A169' },
    { id: 'accounting', title: 'ACCOUNTING', icon: '📊', description: 'Financial reports', color: '#D69E2E' },
    { id: 'warehouse-breakdown', title: 'WAREHOUSE BREAKDOWN', icon: '🏭', description: 'Stock analysis', color: '#6366F1' },
    { id: 'expenses', title: 'EXPENSES', icon: '💰', description: 'Business expenses', color: '#E53E3E' },
    { id: 'settings', title: 'SETTINGS', icon: '⚙️', description: 'System settings & accounts', color: '#A0AEC0' },
  ];
  
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f1a35 0%, #1a2744 50%, #0d1526 100%)',
      padding: '20px',
      color: 'white'
    }}>
      {/* Spin animation for refresh button */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      
      {/* Detail Modal */}
      {detailModal && <DetailModal type={detailModal} onClose={() => setDetailModal(null)} />}
      
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        paddingBottom: '20px',
        borderBottom: '1px solid rgba(184, 134, 11, 0.3)'
      }}>
        <div>
          <h1 style={{margin: 0, color: '#b8860b', fontSize: '28px'}}>📊 Business Dashboard</h1>
          <p style={{margin: '5px 0 0', color: '#9ca3af', fontSize: '14px'}}>
            Welcome back | Real-time business overview
            <span style={{marginLeft: '15px', fontSize: '12px', color: '#6b7280'}}>
              Last updated: {lastRefresh.toLocaleTimeString('en-GB')}
            </span>
            <span style={{
              marginLeft: '10px',
              fontSize: '11px',
              color: wsConnected ? '#22c55e' : '#ef4444',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: wsConnected ? '#22c55e' : '#ef4444',
                animation: wsConnected ? 'pulse 2s infinite' : 'none'
              }}></span>
              {wsConnected ? 'LIVE' : 'Connecting...'}
            </span>
          </p>
        </div>
        <div style={{display: 'flex', gap: '10px'}}>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            style={{
              padding: '10px 20px',
              background: isRefreshing ? 'rgba(184,134,11,0.3)' : 'rgba(184,134,11,0.2)',
              color: '#b8860b',
              border: '1px solid rgba(184,134,11,0.4)',
              borderRadius: '8px',
              cursor: isRefreshing ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span style={{
              display: 'inline-block',
              animation: isRefreshing ? 'spin 1s linear infinite' : 'none'
            }}>🔄</span>
            {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
          </button>
          <button
            onClick={handleDashboardLogout}
            style={{
              padding: '10px 20px',
              background: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Logout
          </button>
        </div>
      </div>
      
      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '25px',
        borderBottom: '2px solid rgba(255,255,255,0.1)',
        paddingBottom: '15px'
      }}>
        <button
          onClick={() => setDashboardTab('summary')}
          style={{
            padding: '12px 30px',
            background: dashboardTab === 'summary' ? 'linear-gradient(135deg, #b8860b 0%, #8b6914 100%)' : 'rgba(255,255,255,0.05)',
            color: dashboardTab === 'summary' ? 'white' : '#9ca3af',
            border: dashboardTab === 'summary' ? 'none' : '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.2s'
          }}
        >
          📈 Summary
        </button>
        {/* Admin tab - only for zobayr (full access) */}
        {hasFullAccess && (
        <button
          onClick={() => setDashboardTab('admin')}
          style={{
            padding: '12px 30px',
            background: dashboardTab === 'admin' ? 'linear-gradient(135deg, #b8860b 0%, #8b6914 100%)' : 'rgba(255,255,255,0.05)',
            color: dashboardTab === 'admin' ? 'white' : '#9ca3af',
            border: dashboardTab === 'admin' ? 'none' : '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.2s'
          }}
        >
          ⚙️ Admin
        </button>
        )}
        {/* Review tab - only for zobayr (full access) */}
        {hasFullAccess && (
        <button
          onClick={() => { setDashboardTab('review'); loadPaymentReview(); }}
          style={{
            padding: '12px 30px',
            background: dashboardTab === 'review' ? 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' : 'rgba(255,255,255,0.05)',
            color: dashboardTab === 'review' ? 'white' : '#9ca3af',
            border: dashboardTab === 'review' ? 'none' : '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.2s'
          }}
        >
          📋 Review
        </button>
        )}
        {/* History tab - only for zobayr (full access) */}
        {hasFullAccess && (
        <button
          onClick={() => { setDashboardTab('history'); loadActionHistory(); }}
          data-testid="history-tab-btn"
          style={{
            padding: '12px 30px',
            background: dashboardTab === 'history' ? 'linear-gradient(135deg, #059669 0%, #047857 100%)' : 'rgba(255,255,255,0.05)',
            color: dashboardTab === 'history' ? 'white' : '#9ca3af',
            border: dashboardTab === 'history' ? 'none' : '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.2s'
          }}
        >
          📜 History
        </button>
        )}
      </div>
      
      {/* Summary Tab Content */}
      {dashboardTab === 'summary' && (
        <>
          {/* Clickable Summary Cards Row - USES liveCalculations for INSTANT SYNC */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            marginBottom: '30px'
          }}>
            <div 
              onClick={() => setDetailModal('total-sales')}
              style={{background: 'rgba(22, 163, 74, 0.1)', border: '1px solid rgba(22, 163, 74, 0.3)', borderRadius: '12px', padding: '20px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s'}}
              onMouseOver={e => e.currentTarget.style.transform = 'translateY(-3px)'}
              onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div style={{fontSize: '14px', color: '#9ca3af', marginBottom: '8px'}}>💰 Total Sales</div>
              <div style={{fontSize: '32px', fontWeight: '700', color: '#22c55e'}}>£{liveCalculations.total_sales?.toLocaleString() || '0'}</div>
              <div style={{fontSize: '11px', color: '#6b7280', marginTop: '8px'}}>Click for details →</div>
            </div>
            <div 
              onClick={() => setDetailModal('cash')}
              style={{background: 'rgba(37, 99, 235, 0.1)', border: '1px solid rgba(37, 99, 235, 0.3)', borderRadius: '12px', padding: '20px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s'}}
              onMouseOver={e => e.currentTarget.style.transform = 'translateY(-3px)'}
              onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div style={{fontSize: '14px', color: '#9ca3af', marginBottom: '8px'}}>💵 Cash Received</div>
              <div style={{fontSize: '32px', fontWeight: '700', color: '#3b82f6'}}>£{liveCalculations.total_cash?.toLocaleString() || '0'}</div>
              <div style={{fontSize: '11px', color: '#6b7280', marginTop: '8px'}}>Click for details →</div>
            </div>
            <div 
              onClick={() => setDetailModal('bank')}
              style={{background: 'rgba(147, 51, 234, 0.1)', border: '1px solid rgba(147, 51, 234, 0.3)', borderRadius: '12px', padding: '20px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s'}}
              onMouseOver={e => e.currentTarget.style.transform = 'translateY(-3px)'}
              onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div style={{fontSize: '14px', color: '#9ca3af', marginBottom: '8px'}}>🏦 Bank Received</div>
              <div style={{fontSize: '32px', fontWeight: '700', color: '#a855f7'}}>£{liveCalculations.total_bank?.toLocaleString() || '0'}</div>
              <div style={{fontSize: '11px', color: '#6b7280', marginTop: '8px'}}>Click for details →</div>
            </div>
            <div 
              onClick={() => setDetailModal('credit')}
              style={{background: 'rgba(220, 38, 38, 0.1)', border: '1px solid rgba(220, 38, 38, 0.3)', borderRadius: '12px', padding: '20px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s'}}
              onMouseOver={e => e.currentTarget.style.transform = 'translateY(-3px)'}
              onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div style={{fontSize: '14px', color: '#9ca3af', marginBottom: '8px'}}>📋 Outstanding Credit</div>
              <div style={{fontSize: '32px', fontWeight: '700', color: '#ef4444'}}>£{liveCalculations.outstanding_credit?.toLocaleString() || '0'}</div>
              <div style={{fontSize: '11px', color: '#6b7280', marginTop: '8px'}}>Click for details →</div>
            </div>
            <div 
              onClick={() => setDetailModal('stock')}
              style={{background: 'rgba(184, 134, 11, 0.1)', border: '1px solid rgba(184, 134, 11, 0.3)', borderRadius: '12px', padding: '20px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s'}}
              onMouseOver={e => e.currentTarget.style.transform = 'translateY(-3px)'}
              onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div style={{fontSize: '14px', color: '#9ca3af', marginBottom: '8px'}}>📦 Stock Value</div>
              <div style={{fontSize: '32px', fontWeight: '700', color: '#b8860b'}}>£{dashboardData?.stock?.total_stock_value?.toLocaleString() || '0'}</div>
              <div style={{fontSize: '11px', color: '#6b7280', marginTop: '8px'}}>Click for details →</div>
            </div>
            <div 
              onClick={() => setDetailModal('orders')}
              style={{background: 'rgba(6, 182, 212, 0.1)', border: '1px solid rgba(6, 182, 212, 0.3)', borderRadius: '12px', padding: '20px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s'}}
              onMouseOver={e => e.currentTarget.style.transform = 'translateY(-3px)'}
              onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div style={{fontSize: '14px', color: '#9ca3af', marginBottom: '8px'}}>📑 Total Orders</div>
              <div style={{fontSize: '32px', fontWeight: '700', color: '#06b6d4'}}>{liveCalculations.total_orders || 0}</div>
              <div style={{fontSize: '11px', color: '#6b7280', marginTop: '8px'}}>Click for details →</div>
            </div>
          </div>

          {/* Charts Row */}
          
          {/* VIEW-ONLY BANNER for non-zobayr users */}
          {isViewOnly && (
            <div style={{
              background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
              border: '2px solid #F59E0B',
              borderRadius: '12px',
              padding: '16px 24px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{fontSize: '24px'}}>👁️</span>
              <div>
                <div style={{fontWeight: '700', color: '#92400E', fontSize: '16px'}}>VIEW-ONLY MODE</div>
                <div style={{color: '#B45309', fontSize: '13px'}}>You can view business data but cannot make changes or access admin features.</div>
              </div>
            </div>
          )}
          
          {/* BOSS-ONLY CARDS: Daily Activity, Deleted Orders, Edited Orders */}
          {/* These show for Zobayr only - not for view-only users */}
          {(() => {
            // Only show admin cards to zobayr (boss), not view-only users
            if (!hasFullAccess) return null;
            
            return (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '20px',
                marginBottom: '30px'
              }}>
                {/* Daily Activity Card */}
                <div 
                  onClick={() => {
                    setDashboardUserInAdminMode(true);
                    setIsAdminLoggedIn(true);
                    setLoggedInAdminUsername('zobayr');
                    setCurrentPage('admin-portal');
                    setActiveTab('daily-activity');
                    setIsDashboardUser(false);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #9F7AEA 0%, #805AD5 100%)',
                    borderRadius: '12px',
                    padding: '24px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(159,122,234,0.3)'
                  }}
                  onMouseOver={e => e.currentTarget.style.transform = 'translateY(-5px)'}
                  onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <div style={{fontSize: '32px', marginBottom: '12px'}}>📅</div>
                  <div style={{fontSize: '18px', fontWeight: '700', color: 'white', marginBottom: '6px'}}>DAILY ACTIVITY</div>
                  <div style={{fontSize: '12px', color: 'rgba(255,255,255,0.8)'}}>Daily orders and payments by customer</div>
                </div>
                
                {/* Deleted Orders Card */}
                <div 
                  onClick={() => {
                    setDashboardUserInAdminMode(true);
                    setIsAdminLoggedIn(true);
                    setLoggedInAdminUsername('zobayr');
                    setCurrentPage('admin-portal');
                    setActiveTab('deleted-orders');
                    setIsDashboardUser(false);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #E53E3E 0%, #C53030 100%)',
                    borderRadius: '12px',
                    padding: '24px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(229,62,62,0.3)'
                  }}
                  onMouseOver={e => e.currentTarget.style.transform = 'translateY(-5px)'}
                  onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <div style={{fontSize: '32px', marginBottom: '12px'}}>🗑️</div>
                  <div style={{fontSize: '18px', fontWeight: '700', color: 'white', marginBottom: '6px'}}>DELETED ORDERS</div>
                  <div style={{fontSize: '12px', color: 'rgba(255,255,255,0.8)'}}>Review and verify deleted orders</div>
                </div>
                
                {/* Edited Orders Card */}
                <div 
                  onClick={() => {
                    setDashboardUserInAdminMode(true);
                    setIsAdminLoggedIn(true);
                    setLoggedInAdminUsername('zobayr');
                    setCurrentPage('admin-portal');
                    setActiveTab('edited-orders');
                    setIsDashboardUser(false);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #ED8936 0%, #DD6B20 100%)',
                    borderRadius: '12px',
                    padding: '24px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(237,137,54,0.3)'
                  }}
                  onMouseOver={e => e.currentTarget.style.transform = 'translateY(-5px)'}
                  onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <div style={{fontSize: '32px', marginBottom: '12px'}}>✏️</div>
                  <div style={{fontSize: '18px', fontWeight: '700', color: 'white', marginBottom: '6px'}}>EDITED ORDERS</div>
                  <div style={{fontSize: '12px', color: 'rgba(255,255,255,0.8)'}}>Review orders that have been modified</div>
                </div>
              </div>
            );
          })()}

          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px', marginBottom: '30px'}}>
            <div style={{background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '12px', padding: '20px'}}>
              <h3 style={{margin: '0 0 10px', color: '#b8860b'}}>📈 Sales vs Expenses</h3>
              <div style={{display: 'flex', gap: '20px', marginBottom: '15px', fontSize: '12px'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}><div style={{width: '12px', height: '12px', background: '#22c55e', borderRadius: '2px'}}></div><span style={{color: '#9ca3af'}}>Sales</span></div>
                <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}><div style={{width: '12px', height: '12px', background: '#ef4444', borderRadius: '2px'}}></div><span style={{color: '#9ca3af'}}>Expenses</span></div>
                <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}><div style={{width: '12px', height: '12px', background: '#3b82f6', borderRadius: '2px'}}></div><span style={{color: '#9ca3af'}}>Profit</span></div>
              </div>
              <div style={{display: 'flex', alignItems: 'flex-end', gap: '8px', height: '200px'}}>
                {(dashboardData.charts?.monthly_data || dashboardData.charts?.monthly_sales || []).map((month, idx) => {
                  const data = dashboardData.charts?.monthly_data || [];
                  const salesValue = month.sales || month.total || 0;
                  const expensesValue = month.expenses || 0;
                  const profitValue = month.profit || (salesValue - expensesValue);
                  // Include profit in maxValue to scale all bars properly
                  const maxValue = Math.max(...data.map(m => Math.max(m.sales || m.total || 0, m.expenses || 0, Math.abs(m.profit || 0))), 1);
                  const salesHeight = (salesValue / maxValue) * 130;
                  const expensesHeight = (expensesValue / maxValue) * 130;
                  const profitHeight = (Math.abs(profitValue) / maxValue) * 130;
                  return (
                    <div key={idx} style={{flex: 1, textAlign: 'center'}}>
                      <div style={{display: 'flex', gap: '2px', justifyContent: 'center', alignItems: 'flex-end', height: '145px'}}>
                        <div style={{width: '14px', height: `${Math.max(salesHeight, 4)}px`, background: 'linear-gradient(180deg, #22c55e 0%, #16a34a 100%)', borderRadius: '3px 3px 0 0'}} title={`Sales: £${salesValue.toLocaleString()}`}></div>
                        <div style={{width: '14px', height: `${Math.max(expensesHeight, 4)}px`, background: 'linear-gradient(180deg, #ef4444 0%, #dc2626 100%)', borderRadius: '3px 3px 0 0'}} title={`Expenses: £${expensesValue.toLocaleString()}`}></div>
                        <div style={{width: '14px', height: `${Math.max(profitHeight, 4)}px`, background: profitValue >= 0 ? 'linear-gradient(180deg, #3b82f6 0%, #2563eb 100%)' : 'linear-gradient(180deg, #f59e0b 0%, #d97706 100%)', borderRadius: '3px 3px 0 0'}} title={`Profit: £${profitValue.toLocaleString()}`}></div>
                      </div>
                      <div style={{fontSize: '10px', color: '#9ca3af', marginTop: '5px'}}>{month.month}</div>
                      <div style={{fontSize: '9px', color: profitValue >= 0 ? '#3b82f6' : '#f59e0b', fontWeight: '600'}}>
                        {profitValue >= 0 ? '+' : ''}£{(profitValue/1000).toFixed(1)}k
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '12px', padding: '20px'}}>
              <h3 style={{margin: '0 0 20px', color: '#b8860b'}}>🏆 Top Selling Products</h3>
              {(liveCalculations.top_products || dashboardData.charts?.top_products || []).map((product, idx) => (
                <div key={idx} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: 'rgba(255, 255, 255, 0.03)', borderRadius: '8px', marginBottom: '8px'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                    <span style={{width: '30px', height: '30px', background: idx === 0 ? '#fbbf24' : idx === 1 ? '#9ca3af' : idx === 2 ? '#cd7f32' : '#4b5563', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '14px', color: '#000'}}>{idx + 1}</span>
                    <span style={{color: '#e5e7eb'}}>{product.name}</span>
                  </div>
                  <span style={{color: '#22c55e', fontWeight: '600'}}>£{product.value?.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Business Report Download Section */}
          <BusinessReportDownload API={API} />

          {/* Bottom Stats */}
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px'}}>
            <div style={{background: 'rgba(220, 38, 38, 0.1)', border: '1px solid rgba(220, 38, 38, 0.3)', borderRadius: '12px', padding: '20px'}}>
              <h3 style={{margin: '0 0 15px', color: '#ef4444'}}>⚠️ Low Stock Alert ({dashboardData.stock?.low_stock_count || 0})</h3>
              <div style={{maxHeight: '200px', overflowY: 'auto'}}>
                {(dashboardData.stock?.low_stock_items || []).slice(0, 5).map((item, idx) => (
                  <div key={idx} style={{display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', marginBottom: '6px', fontSize: '13px'}}>
                    <span style={{color: '#e5e7eb'}}>{item.product} {item.variation}</span>
                    <span style={{color: '#ef4444', fontWeight: '600'}}>{item.stock} left</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '12px', padding: '20px'}}>
              <h3 style={{margin: '0 0 15px', color: '#b8860b'}}>📊 Quick Stats</h3>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
                <div style={{background: 'rgba(0,0,0,0.2)', padding: '15px', borderRadius: '8px', textAlign: 'center'}}>
                  <div style={{fontSize: '28px', fontWeight: '700', color: '#22c55e'}}>{dashboardData.customers?.total_customers || 0}</div>
                  <div style={{fontSize: '12px', color: '#9ca3af'}}>Total Customers</div>
                </div>
                <div style={{background: 'rgba(0,0,0,0.2)', padding: '15px', borderRadius: '8px', textAlign: 'center'}}>
                  <div style={{fontSize: '28px', fontWeight: '700', color: '#ef4444'}}>{liveCalculations.active_credit_accounts || 0}</div>
                  <div style={{fontSize: '12px', color: '#9ca3af'}}>Credit Accounts</div>
                </div>
                <div style={{background: 'rgba(0,0,0,0.2)', padding: '15px', borderRadius: '8px', textAlign: 'center'}}>
                  <div style={{fontSize: '28px', fontWeight: '700', color: '#b8860b'}}>{dashboardData.stock?.total_products || 0}</div>
                  <div style={{fontSize: '12px', color: '#9ca3af'}}>Products</div>
                </div>
                <div style={{background: 'rgba(0,0,0,0.2)', padding: '15px', borderRadius: '8px', textAlign: 'center'}}>
                  <div style={{fontSize: '28px', fontWeight: '700', color: '#f59e0b'}}>{dashboardData.sales?.pending_orders || 0}</div>
                  <div style={{fontSize: '12px', color: '#9ca3af'}}>Pending Orders</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Credit Accounts Section - FULL HISTORY WITH PDF EXPORT */}
          <div style={{background: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '12px', padding: '20px', marginTop: '30px', marginBottom: '30px'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
              <h3 style={{margin: 0, color: '#ef4444', display: 'flex', alignItems: 'center', gap: '10px'}}>
                💳 Credit Accounts History
                <span style={{background: 'rgba(239,68,68,0.2)', padding: '2px 10px', borderRadius: '12px', fontSize: '14px'}}>
                  {liveCalculations.credit_accounts?.length || 0} customers
                </span>
                <span style={{background: 'rgba(245,158,11,0.2)', padding: '2px 10px', borderRadius: '12px', fontSize: '14px', color: '#f59e0b'}}>
                  {liveCalculations.active_credit_accounts || 0} with balance
                </span>
              </h3>
              <div style={{display: 'flex', gap: '10px'}}>
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('dashboard_token');
                      const response = await axios.get(`${API}/api/credit-accounts/export-pdf-full`, {
                        headers: { 'Authorization': `Bearer ${token}` },
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `credit_history_${new Date().toISOString().split('T')[0]}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                    } catch (error) {
                      console.error('Error exporting PDF:', error);
                      alert('Error exporting PDF. Please try again.');
                    }
                  }}
                  style={{
                    background: '#22c55e',
                    border: 'none',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  📥 Export Full History PDF
                </button>
                {hasFullAccess && (
                <button
                  onClick={() => {
                    setDashboardUserInAdminMode(true);
                    setIsAdminLoggedIn(true);
                    setLoggedInAdminUsername('zobayr');
                    setCurrentPage('admin-portal');
                    setActiveTab('credit-accounts');
                    setIsDashboardUser(false);
                  }}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(239,68,68,0.4)',
                    color: '#ef4444',
                    padding: '6px 14px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  View Admin Details →
                </button>
                )}
              </div>
            </div>
            
            {(liveCalculations.credit_accounts || []).length === 0 ? (
              <div style={{textAlign: 'center', padding: '30px', color: '#9ca3af'}}>
                <div style={{fontSize: '32px', marginBottom: '10px'}}>📋</div>
                <div>No credit transactions found</div>
              </div>
            ) : (
              <div style={{maxHeight: '400px', overflowY: 'auto'}}>
                <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '13px'}}>
                  <thead style={{position: 'sticky', top: 0, background: '#1a2744'}}>
                    <tr>
                      <th style={{padding: '10px', textAlign: 'left', color: '#9ca3af', borderBottom: '1px solid rgba(255,255,255,0.1)'}}>Customer</th>
                      <th style={{padding: '10px', textAlign: 'right', color: '#9ca3af', borderBottom: '1px solid rgba(255,255,255,0.1)'}}>Total Credit</th>
                      <th style={{padding: '10px', textAlign: 'right', color: '#9ca3af', borderBottom: '1px solid rgba(255,255,255,0.1)'}}>Paid</th>
                      <th style={{padding: '10px', textAlign: 'right', color: '#9ca3af', borderBottom: '1px solid rgba(255,255,255,0.1)'}}>Balance</th>
                      <th style={{padding: '10px', textAlign: 'center', color: '#9ca3af', borderBottom: '1px solid rgba(255,255,255,0.1)'}}>Orders</th>
                      <th style={{padding: '10px', textAlign: 'center', color: '#9ca3af', borderBottom: '1px solid rgba(255,255,255,0.1)'}}>Status</th>
                      <th style={{padding: '10px', textAlign: 'center', color: '#9ca3af', borderBottom: '1px solid rgba(255,255,255,0.1)'}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(liveCalculations.credit_accounts || []).map((customer, idx) => (
                      <tr key={idx} style={{borderBottom: '1px solid rgba(255,255,255,0.05)'}}>
                        <td style={{padding: '12px 10px'}}>
                          <div style={{fontWeight: '600', color: '#e5e7eb'}}>{customer.name}</div>
                          {customer.company && customer.company !== customer.name && (
                            <div style={{fontSize: '11px', color: '#6b7280'}}>{customer.company}</div>
                          )}
                          {customer.phone && <div style={{fontSize: '11px', color: '#6b7280'}}>{customer.phone}</div>}
                        </td>
                        <td style={{padding: '12px 10px', textAlign: 'right', color: '#f59e0b', fontWeight: '600'}}>
                          £{customer.total_credit_taken?.toFixed(2)}
                        </td>
                        <td style={{padding: '12px 10px', textAlign: 'right', color: '#22c55e', fontWeight: '600'}}>
                          £{customer.total_paid?.toFixed(2)}
                        </td>
                        <td style={{padding: '12px 10px', textAlign: 'right', fontWeight: '700', color: customer.outstanding_balance > 0 ? '#ef4444' : '#22c55e'}}>
                          £{customer.outstanding_balance?.toFixed(2)}
                        </td>
                        <td style={{padding: '12px 10px', textAlign: 'center', color: '#9ca3af'}}>
                          {customer.orders?.length || 0}
                        </td>
                        <td style={{padding: '12px 10px', textAlign: 'center'}}>
                          {customer.outstanding_balance > 0 ? (
                            <span style={{background: 'rgba(239,68,68,0.2)', color: '#ef4444', padding: '2px 8px', borderRadius: '10px', fontSize: '11px'}}>
                              UNPAID
                            </span>
                          ) : (
                            <span style={{background: 'rgba(34,197,94,0.2)', color: '#22c55e', padding: '2px 8px', borderRadius: '10px', fontSize: '11px'}}>
                              CLEARED
                            </span>
                          )}
                        </td>
                        <td style={{padding: '12px 10px', textAlign: 'center'}}>
                          {customer.customer_id && (
                            <button
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('dashboard_token');
                                  const response = await axios.get(`${API}/api/credit-accounts/export-pdf-individual/${customer.customer_id}`, {
                                    headers: { 'Authorization': `Bearer ${token}` },
                                    responseType: 'blob'
                                  });
                                  const url = window.URL.createObjectURL(new Blob([response.data]));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  const safeName = (customer.name || 'Customer').replace(/[^a-zA-Z0-9]/g, '_');
                                  link.setAttribute('download', `credit_history_${safeName}_${new Date().toISOString().split('T')[0]}.pdf`);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                } catch (error) {
                                  console.error('Error exporting individual PDF:', error);
                                  alert('Error exporting PDF. Please try again.');
                                }
                              }}
                              style={{
                                background: 'rgba(59, 130, 246, 0.2)',
                                border: '1px solid rgba(59, 130, 246, 0.4)',
                                color: '#3b82f6',
                                padding: '4px 10px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                cursor: 'pointer',
                                fontWeight: '500',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              title={`Download full credit history for ${customer.name}`}
                            >
                              📥 PDF
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Summary Row */}
            <div style={{marginTop: '15px', paddingTop: '15px', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px'}}>
              <div style={{textAlign: 'center'}}>
                <div style={{fontSize: '11px', color: '#9ca3af'}}>Total Credit Taken</div>
                <div style={{fontSize: '18px', fontWeight: '700', color: '#f59e0b'}}>
                  £{(liveCalculations.credit_accounts || []).reduce((sum, c) => sum + (c.total_credit_taken || 0), 0).toFixed(2)}
                </div>
              </div>
              <div style={{textAlign: 'center'}}>
                <div style={{fontSize: '11px', color: '#9ca3af'}}>Total Paid</div>
                <div style={{fontSize: '18px', fontWeight: '700', color: '#22c55e'}}>
                  £{(liveCalculations.credit_accounts || []).reduce((sum, c) => sum + (c.total_paid || 0), 0).toFixed(2)}
                </div>
              </div>
              <div style={{textAlign: 'center'}}>
                <div style={{fontSize: '11px', color: '#9ca3af'}}>Outstanding</div>
                <div style={{fontSize: '18px', fontWeight: '700', color: '#ef4444'}}>
                  £{liveCalculations.outstanding_credit?.toFixed(2) || '0.00'}
                </div>
              </div>
              <div style={{textAlign: 'center'}}>
                <div style={{fontSize: '11px', color: '#9ca3af'}}>Collection Rate</div>
                <div style={{fontSize: '18px', fontWeight: '700', color: '#3b82f6'}}>
                  {(() => {
                    const totalCredit = (liveCalculations.credit_accounts || []).reduce((sum, c) => sum + (c.total_credit_taken || 0), 0);
                    const totalPaid = (liveCalculations.credit_accounts || []).reduce((sum, c) => sum + (c.total_paid || 0), 0);
                    return totalCredit > 0 ? ((totalPaid / totalCredit) * 100).toFixed(1) : '0';
                  })()}%
                </div>
              </div>
            </div>
          </div>
          
          <div style={{textAlign: 'center', marginTop: '40px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)', color: '#6b7280', fontSize: '12px'}}>
            Plush Living LTD Dashboard • Last updated: {new Date().toLocaleString('en-GB')}
          </div>
        </>
      )}
      
      {/* Admin Tab Content */}
      {dashboardTab === 'admin' && (
        <div>
          <div style={{marginBottom: '20px'}}>
            <h2 style={{color: '#b8860b', margin: '0 0 10px'}}>⚙️ Admin Functions</h2>
            <p style={{color: '#9ca3af', fontSize: '14px', margin: 0}}>Access all administrative features</p>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px'
          }}>
            {dashboardAdminSections.map((section) => (
              <div
                key={section.id}
                onClick={() => {
                  // Set admin logged in and navigate to the section
                  // Keep track that this is a dashboard user accessing admin
                  setDashboardUserInAdminMode(true);
                  setIsAdminLoggedIn(true);
                  setLoggedInAdminUsername('zobayr');
                  setCurrentPage('admin-portal');
                  setActiveTab(section.id);
                  setIsDashboardUser(false);
                }}
                style={{
                  background: `${section.color}15`,
                  border: `1px solid ${section.color}40`,
                  borderRadius: '12px',
                  padding: '25px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={e => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = `0 10px 30px ${section.color}30`;
                }}
                onMouseOut={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{fontSize: '36px', marginBottom: '12px'}}>{section.icon}</div>
                <h3 style={{margin: '0 0 8px', color: section.color, fontSize: '16px', fontWeight: '700'}}>{section.title}</h3>
                <p style={{margin: 0, color: '#9ca3af', fontSize: '13px'}}>{section.description}</p>
              </div>
            ))}
          </div>
          <div style={{textAlign: 'center', marginTop: '40px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)', color: '#6b7280', fontSize: '12px'}}>
            Plush Living LTD Dashboard • Last updated: {new Date().toLocaleString('en-GB')}
          </div>
        </div>
      )}
      
      {/* Review Tab Content - Complete Payment History */}
      {dashboardTab === 'review' && (
        <div>
          <div style={{marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <div>
              <h2 style={{color: '#7c3aed', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: '10px'}}>
                📋 Payment Review
              </h2>
              <p style={{color: '#9ca3af', fontSize: '14px', margin: 0}}>
                Review and approve transactions • {paymentReview.approved_count || 0} already approved
              </p>
            </div>
            <div style={{display: 'flex', gap: '10px'}}>
              <button
                onClick={loadPaymentReview}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                🔄 Refresh
              </button>
              {paymentReview.transactions?.length > 0 && (
                <button
                  onClick={approveAllTransactions}
                  style={{
                    background: '#22c55e',
                    border: 'none',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  ✓ Approve All
                </button>
              )}
            </div>
          </div>
          
          {/* Summary Stats */}
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '15px', marginBottom: '25px'}}>
            <div style={{background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '10px', padding: '15px', textAlign: 'center'}}>
              <div style={{fontSize: '24px', fontWeight: '700', color: '#ef4444'}}>£{(paymentReview.summary?.total_credit_taken || 0).toLocaleString()}</div>
              <div style={{fontSize: '11px', color: '#9ca3af'}}>Credit Taken</div>
            </div>
            <div style={{background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '10px', padding: '15px', textAlign: 'center'}}>
              <div style={{fontSize: '24px', fontWeight: '700', color: '#22c55e'}}>£{(paymentReview.summary?.total_cash_received || 0).toLocaleString()}</div>
              <div style={{fontSize: '11px', color: '#9ca3af'}}>Cash Received</div>
            </div>
            <div style={{background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '10px', padding: '15px', textAlign: 'center'}}>
              <div style={{fontSize: '24px', fontWeight: '700', color: '#3b82f6'}}>£{(paymentReview.summary?.total_bank_received || 0).toLocaleString()}</div>
              <div style={{fontSize: '11px', color: '#9ca3af'}}>Bank Received</div>
            </div>
            <div style={{background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.3)', borderRadius: '10px', padding: '15px', textAlign: 'center'}}>
              <div style={{fontSize: '24px', fontWeight: '700', color: '#7c3aed'}}>£{(paymentReview.summary?.total_credit_payments || 0).toLocaleString()}</div>
              <div style={{fontSize: '11px', color: '#9ca3af'}}>Credit Payments</div>
            </div>
            <div style={{background: 'rgba(184,134,11,0.1)', border: '1px solid rgba(184,134,11,0.3)', borderRadius: '10px', padding: '15px', textAlign: 'center'}}>
              <div style={{fontSize: '24px', fontWeight: '700', color: '#b8860b'}}>£{(paymentReview.summary?.total_received || 0).toLocaleString()}</div>
              <div style={{fontSize: '11px', color: '#9ca3af'}}>Total Received</div>
            </div>
          </div>
          
          {/* Transaction List */}
          {loadingReview ? (
            <div style={{textAlign: 'center', padding: '40px', color: '#9ca3af'}}>Loading transactions...</div>
          ) : paymentReview.transactions?.length === 0 ? (
            <div style={{textAlign: 'center', padding: '60px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px'}}>
              <div style={{fontSize: '48px', marginBottom: '15px'}}>📋</div>
              <h3 style={{color: '#9ca3af', margin: '0 0 10px'}}>No Transactions Yet</h3>
              <p style={{color: '#6b7280', margin: 0}}>Transactions will appear here once orders are created.</p>
            </div>
          ) : (
            <div style={{display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '600px', overflowY: 'auto'}}>
              {paymentReview.transactions?.map((txn) => (
                <div
                  key={txn.id}
                  style={{
                    background: txn.type === 'CREDIT_TAKEN' ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)',
                    border: `1px solid ${txn.type === 'CREDIT_TAKEN' ? 'rgba(239,68,68,0.2)' : 'rgba(34,197,94,0.2)'}`,
                    borderRadius: '8px',
                    padding: '12px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      background: txn.type === 'CREDIT_TAKEN' ? 'rgba(239,68,68,0.2)' : 
                                 txn.type === 'PAYMENT_CASH' ? 'rgba(34,197,94,0.2)' :
                                 txn.type === 'PAYMENT_BANK' ? 'rgba(59,130,246,0.2)' :
                                 'rgba(124,58,237,0.2)'
                    }}>
                      {txn.type === 'CREDIT_TAKEN' ? '📋' : 
                       txn.type === 'PAYMENT_CASH' ? '💵' :
                       txn.type === 'PAYMENT_BANK' ? '🏦' : '💳'}
                    </div>
                    <div>
                      <div style={{fontWeight: '600', color: '#e5e7eb', fontSize: '14px'}}>
                        {txn.customer_name}
                      </div>
                      <div style={{fontSize: '12px', color: '#6b7280'}}>
                        {txn.type === 'CREDIT_TAKEN' ? 
                          `Credit taken for bill #${txn.order_id?.slice(0,8)}` :
                          `£${txn.amount?.toFixed(2)} paid for bill #${txn.order_id?.slice(0,8)}`
                        }
                      </div>
                      <div style={{fontSize: '11px', color: '#4b5563'}}>
                        {txn.timestamp ? new Date(txn.timestamp).toLocaleString('en-GB') : 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                    <button
                      onClick={() => viewBill(txn.order_id)}
                      style={{
                        background: 'transparent',
                        border: '1px solid rgba(124,58,237,0.4)',
                        color: '#a78bfa',
                        padding: '6px 12px',
                        borderRadius: '5px',
                        fontSize: '11px',
                        cursor: 'pointer'
                      }}
                      data-testid={`view-bill-${txn.order_id}`}
                    >
                      View Bill
                    </button>
                    {/* Only show Reject button for payments, not for CREDIT_TAKEN, and not for already approved */}
                    {txn.type !== 'CREDIT_TAKEN' && !txn.approved && (
                      <button
                        onClick={() => rejectTransaction(txn)}
                        style={{
                          background: '#ef4444',
                          border: 'none',
                          color: 'white',
                          padding: '6px 12px',
                          borderRadius: '5px',
                          fontSize: '11px',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                        data-testid={`reject-payment-${txn.id}`}
                      >
                        ✗ Reject
                      </button>
                    )}
                    {txn.approved ? (
                      <span
                        style={{
                          background: 'rgba(34,197,94,0.15)',
                          border: '1px solid rgba(34,197,94,0.3)',
                          color: '#22c55e',
                          padding: '6px 12px',
                          borderRadius: '5px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}
                      >
                        ✓ Approved
                      </span>
                    ) : (
                      <button
                        onClick={() => approveTransaction(txn.id)}
                        style={{
                          background: '#22c55e',
                          border: 'none',
                          color: 'white',
                          padding: '6px 12px',
                          borderRadius: '5px',
                          fontSize: '11px',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        ✓ Approve
                      </button>
                    )}
                    <div style={{textAlign: 'right', minWidth: '80px'}}>
                      <div style={{
                        fontSize: '18px',
                        fontWeight: '700',
                        color: txn.type === 'CREDIT_TAKEN' ? '#ef4444' : '#22c55e'
                      }}>
                        {txn.type === 'CREDIT_TAKEN' ? '-' : '+'}£{txn.amount?.toFixed(2)}
                      </div>
                      <div style={{
                        fontSize: '10px',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        background: txn.type === 'CREDIT_TAKEN' ? 'rgba(239,68,68,0.2)' : 
                                   txn.type === 'PAYMENT_CASH' ? 'rgba(34,197,94,0.2)' :
                                   txn.type === 'PAYMENT_BANK' ? 'rgba(59,130,246,0.2)' :
                                   'rgba(124,58,237,0.2)',
                        color: txn.type === 'CREDIT_TAKEN' ? '#ef4444' : 
                               txn.type === 'PAYMENT_CASH' ? '#22c55e' :
                               txn.type === 'PAYMENT_BANK' ? '#3b82f6' : '#7c3aed'
                      }}>
                        {txn.type === 'CREDIT_TAKEN' ? 'Credit Taken' : 
                         txn.type === 'PAYMENT_CASH' ? 'Cash' :
                         txn.type === 'PAYMENT_BANK' ? 'Bank' : 'Credit Payment'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div style={{textAlign: 'center', marginTop: '40px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)', color: '#6b7280', fontSize: '12px'}}>
            Plush Living LTD Payment Review • {paymentReview.total || 0} transactions
          </div>
        </div>
      )}
      
      {/* Bill View Modal */}
      {selectedBillId && (
        <div 
          className="modal-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setSelectedBillId(null)}
        >
          <div 
            style={{
              background: 'linear-gradient(135deg, #0f1a35 0%, #1a2744 100%)',
              borderRadius: '16px',
              maxWidth: '700px',
              width: '95%',
              maxHeight: '90vh',
              overflow: 'auto',
              border: '1px solid rgba(184,134,11,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #b8860b 0%, #8b6914 100%)',
              padding: '20px',
              borderRadius: '16px 16px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{margin: 0, color: 'white', fontSize: '18px'}}>
                📄 Bill #{selectedBillId.id?.slice(0,8)}
              </h2>
              <button
                onClick={() => setSelectedBillId(null)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  color: 'white',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '18px'
                }}
              >
                ×
              </button>
            </div>
            
            {/* Modal Content */}
            <div style={{padding: '25px'}}>
              {/* Customer Info */}
              <div style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '12px',
                padding: '15px',
                marginBottom: '20px'
              }}>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
                  <div>
                    <div style={{fontSize: '12px', color: '#9ca3af'}}>Customer</div>
                    <div style={{fontSize: '16px', fontWeight: '600', color: '#e5e7eb'}}>
                      {selectedBillId.customer_name || 'Guest Customer'}
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize: '12px', color: '#9ca3af'}}>Customer #</div>
                    <div style={{fontSize: '16px', fontWeight: '600', color: '#b8860b'}}>
                      {selectedBillId.customer_number || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize: '12px', color: '#9ca3af'}}>Date</div>
                    <div style={{fontSize: '14px', color: '#e5e7eb'}}>
                      {selectedBillId.date ? new Date(selectedBillId.date).toLocaleDateString('en-GB') : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize: '12px', color: '#9ca3af'}}>Payment Method</div>
                    <div style={{fontSize: '14px', color: '#e5e7eb', textTransform: 'capitalize'}}>
                      {selectedBillId.payment_method || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Items Table */}
              <div style={{marginBottom: '20px'}}>
                <h4 style={{color: '#b8860b', marginBottom: '10px', fontSize: '14px'}}>📦 Items</h4>
                <table style={{width: '100%', borderCollapse: 'collapse'}}>
                  <thead>
                    <tr style={{borderBottom: '1px solid rgba(255,255,255,0.1)'}}>
                      <th style={{padding: '10px', textAlign: 'left', color: '#9ca3af', fontSize: '12px'}}>Product</th>
                      <th style={{padding: '10px', textAlign: 'center', color: '#9ca3af', fontSize: '12px'}}>Qty</th>
                      <th style={{padding: '10px', textAlign: 'right', color: '#9ca3af', fontSize: '12px'}}>Price</th>
                      <th style={{padding: '10px', textAlign: 'right', color: '#9ca3af', fontSize: '12px'}}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedBillId.items || []).map((item, idx) => (
                      <tr key={idx} style={{borderBottom: '1px solid rgba(255,255,255,0.05)'}}>
                        <td style={{padding: '10px', color: '#e5e7eb', fontSize: '13px'}}>
                          {item.product_name}
                          {item.variant_name && <span style={{color: '#9ca3af', fontSize: '11px'}}> ({item.variant_name})</span>}
                        </td>
                        <td style={{padding: '10px', textAlign: 'center', color: '#e5e7eb', fontSize: '13px'}}>
                          {item.quantity}
                        </td>
                        <td style={{padding: '10px', textAlign: 'right', color: '#e5e7eb', fontSize: '13px'}}>
                          £{parseFloat(item.unit_price || 0).toFixed(2)}
                        </td>
                        <td style={{padding: '10px', textAlign: 'right', color: '#e5e7eb', fontSize: '13px'}}>
                          £{(parseFloat(item.quantity || 0) * parseFloat(item.unit_price || 0)).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Totals */}
              <div style={{
                background: 'rgba(184,134,11,0.1)',
                borderRadius: '12px',
                padding: '15px',
                border: '1px solid rgba(184,134,11,0.3)'
              }}>
                <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span style={{color: '#9ca3af'}}>Total Stock:</span>
                    <span style={{color: '#e5e7eb'}}>£{parseFloat(selectedBillId.subtotal || 0).toFixed(2)}</span>
                  </div>
                  {parseFloat(selectedBillId.cut_piece_charge || 0) > 0 && (
                    <div style={{display: 'flex', justifyContent: 'space-between'}}>
                      <span style={{color: '#9ca3af'}}>Cutting Cost:</span>
                      <span style={{color: '#e5e7eb'}}>£{parseFloat(selectedBillId.cut_piece_charge || 0).toFixed(2)}</span>
                    </div>
                  )}
                  {parseFloat(selectedBillId.fitting_charge || 0) > 0 && (
                    <div style={{display: 'flex', justifyContent: 'space-between'}}>
                      <span style={{color: '#9ca3af'}}>Fitting Cost:</span>
                      <span style={{color: '#e5e7eb'}}>£{parseFloat(selectedBillId.fitting_charge || 0).toFixed(2)}</span>
                    </div>
                  )}
                  {parseFloat(selectedBillId.vat_amount || 0) > 0 && (
                    <div style={{display: 'flex', justifyContent: 'space-between'}}>
                      <span style={{color: '#9ca3af'}}>VAT:</span>
                      <span style={{color: '#e5e7eb'}}>£{parseFloat(selectedBillId.vat_amount || 0).toFixed(2)}</span>
                    </div>
                  )}
                  {parseFloat(selectedBillId.discount || 0) > 0 && (
                    <div style={{display: 'flex', justifyContent: 'space-between'}}>
                      <span style={{color: '#9ca3af'}}>Discount:</span>
                      <span style={{color: '#ef4444'}}>-£{parseFloat(selectedBillId.discount || 0).toFixed(2)}</span>
                    </div>
                  )}
                  <div style={{display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px', marginTop: '5px'}}>
                    <span style={{color: '#b8860b', fontWeight: '600'}}>Grand Total:</span>
                    <span style={{color: '#b8860b', fontWeight: '700', fontSize: '18px'}}>£{parseFloat(selectedBillId.grand_total || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              {/* Payment Status */}
              <div style={{
                marginTop: '20px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '12px',
                padding: '15px'
              }}>
                <h4 style={{color: '#b8860b', marginBottom: '10px', fontSize: '14px'}}>💳 Payment Status</h4>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
                  <div>
                    <div style={{fontSize: '12px', color: '#9ca3af'}}>Cash Paid</div>
                    <div style={{fontSize: '16px', color: '#22c55e'}}>£{parseFloat(selectedBillId.cash_paid_amount || 0).toFixed(2)}</div>
                  </div>
                  <div>
                    <div style={{fontSize: '12px', color: '#9ca3af'}}>Bank Paid</div>
                    <div style={{fontSize: '16px', color: '#3b82f6'}}>£{parseFloat(selectedBillId.bank_paid_amount || 0).toFixed(2)}</div>
                  </div>
                  <div>
                    <div style={{fontSize: '12px', color: '#9ca3af'}}>Credit Paid</div>
                    <div style={{fontSize: '16px', color: '#a855f7'}}>£{parseFloat(selectedBillId.credit_paid_amount || 0).toFixed(2)}</div>
                  </div>
                  <div>
                    <div style={{fontSize: '12px', color: '#9ca3af'}}>Outstanding Balance</div>
                    <div style={{fontSize: '16px', color: parseFloat(selectedBillId.credit_balance || 0) > 0 ? '#ef4444' : '#22c55e', fontWeight: '600'}}>
                      £{parseFloat(selectedBillId.credit_balance || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Notes */}
              {selectedBillId.notes && (
                <div style={{
                  marginTop: '20px',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '12px',
                  padding: '15px'
                }}>
                  <h4 style={{color: '#b8860b', marginBottom: '10px', fontSize: '14px'}}>📝 Notes</h4>
                  <p style={{color: '#e5e7eb', margin: 0, fontSize: '13px'}}>{selectedBillId.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* 🔒 History Tab Content - IMMUTABLE ACTION LOG */}
      {dashboardTab === 'history' && (
        <div style={{
          background: 'rgba(255,255,255,0.02)',
          borderRadius: '16px',
          border: '1px solid rgba(255,255,255,0.1)',
          padding: '30px'
        }}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px'}}>
            <div>
              <h2 style={{color: '#10b981', margin: 0, fontSize: '24px'}}>📜 Action History</h2>
              <p style={{color: '#6b7280', margin: '5px 0 0 0', fontSize: '13px'}}>
                🔒 Immutable log - Cannot be modified or deleted
              </p>
            </div>
            <button
              onClick={loadActionHistory}
              disabled={historyLoading}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '13px'
              }}
            >
              {historyLoading ? '⏳ Loading...' : '🔄 Refresh'}
            </button>
          </div>
          
          {/* Summary Cards */}
          {historySummary && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '15px',
              marginBottom: '25px'
            }}>
              <div style={{background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '12px', padding: '15px', textAlign: 'center'}}>
                <div style={{fontSize: '12px', color: '#9ca3af'}}>Total Actions</div>
                <div style={{fontSize: '24px', fontWeight: '700', color: '#10b981'}}>{historySummary.total_actions || 0}</div>
              </div>
              <div style={{background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '12px', padding: '15px', textAlign: 'center'}}>
                <div style={{fontSize: '12px', color: '#9ca3af'}}>Today</div>
                <div style={{fontSize: '24px', fontWeight: '700', color: '#3b82f6'}}>{historySummary.today_actions || 0}</div>
              </div>
            </div>
          )}
          
          {/* History Table */}
          <div style={{
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '12px',
            overflow: 'hidden',
            maxHeight: '600px',
            overflowY: 'auto'
          }}>
            {historyLoading ? (
              <div style={{padding: '40px', textAlign: 'center', color: '#9ca3af'}}>
                ⏳ Loading action history...
              </div>
            ) : actionHistory.length > 0 ? (
              <table style={{width: '100%', borderCollapse: 'collapse'}}>
                <thead>
                  <tr style={{background: 'rgba(16, 185, 129, 0.2)'}}>
                    <th style={{padding: '15px', textAlign: 'left', color: '#10b981', fontSize: '12px', fontWeight: '600'}}>TIMESTAMP</th>
                    <th style={{padding: '15px', textAlign: 'left', color: '#10b981', fontSize: '12px', fontWeight: '600'}}>ACTION</th>
                    <th style={{padding: '15px', textAlign: 'left', color: '#10b981', fontSize: '12px', fontWeight: '600'}}>USER</th>
                    <th style={{padding: '15px', textAlign: 'left', color: '#10b981', fontSize: '12px', fontWeight: '600'}}>DETAILS</th>
                  </tr>
                </thead>
                <tbody>
                  {actionHistory.map((entry, idx) => (
                    <tr key={entry.id || idx} style={{borderBottom: '1px solid rgba(255,255,255,0.05)'}}>
                      <td style={{padding: '12px 15px', color: '#9ca3af', fontSize: '12px'}}>
                        {new Date(entry.timestamp).toLocaleString()}
                      </td>
                      <td style={{padding: '12px 15px'}}>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600',
                          background: entry.action_type?.includes('CREATE') ? 'rgba(34, 197, 94, 0.2)' :
                                     entry.action_type?.includes('DELETE') ? 'rgba(239, 68, 68, 0.2)' :
                                     entry.action_type?.includes('LOGIN') ? 'rgba(59, 130, 246, 0.2)' :
                                     entry.action_type?.includes('REJECT') ? 'rgba(249, 115, 22, 0.2)' :
                                     entry.action_type?.includes('FULFILL') ? 'rgba(168, 85, 247, 0.2)' :
                                     'rgba(107, 114, 128, 0.2)',
                          color: entry.action_type?.includes('CREATE') ? '#22c55e' :
                                entry.action_type?.includes('DELETE') ? '#ef4444' :
                                entry.action_type?.includes('LOGIN') ? '#3b82f6' :
                                entry.action_type?.includes('REJECT') ? '#f97316' :
                                entry.action_type?.includes('FULFILL') ? '#a855f7' :
                                '#9ca3af'
                        }}>
                          {entry.action_type}
                        </span>
                      </td>
                      <td style={{padding: '12px 15px', color: '#e5e7eb', fontSize: '13px', fontWeight: '500'}}>
                        {entry.performed_by}
                      </td>
                      <td style={{padding: '12px 15px', color: '#9ca3af', fontSize: '12px'}}>
                        {entry.details?.invoice_number && <span style={{color: '#b8860b', fontWeight: '600'}}>Bill #{entry.details.invoice_number} </span>}
                        {entry.details?.bill_date && <span>({entry.details.bill_date}) </span>}
                        {entry.details?.customer_name && <span>• {entry.details.customer_name} </span>}
                        {entry.details?.bill_total && <span>• Bill Total: £{parseFloat(entry.details.bill_total).toFixed(2)} </span>}
                        {entry.details?.payment_amount && <span style={{color: '#22c55e'}}>• Payment: £{parseFloat(entry.details.payment_amount).toFixed(2)} </span>}
                        {entry.details?.grand_total && !entry.details?.bill_total && <span>• £{parseFloat(entry.details.grand_total).toFixed(2)} </span>}
                        {entry.details?.rejected_amount && <span style={{color: '#ef4444'}}>• Rejected: £{parseFloat(entry.details.rejected_amount).toFixed(2)} </span>}
                        {entry.details?.new_credit_balance !== undefined && <span>• Balance: £{parseFloat(entry.details.new_credit_balance).toFixed(2)} </span>}
                        {entry.details?.login_type && <span>({entry.details.login_type})</span>}
                        {entry.details?.order_id && !entry.details?.invoice_number && <span style={{color: '#6b7280'}}>ID: {entry.details.order_id.substring(0, 8)}...</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{padding: '40px', textAlign: 'center', color: '#9ca3af'}}>
                📜 No action history recorded yet. Actions will appear here as they happen.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardUserView;
